import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { EMPTY, Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { BuyContractService } from '../buy-contract.service';
import { BuyContractStatus, BuyContractAllocation, PublicBuyContractResponse } from '../buy-contract.type';
import { AllocationGroupsList } from '../allocation-groups-list/allocation-groups-list';

const POLL_INTERVAL_MS = 20_000;

@Component({
  selector: 'app-contract-public-status-buyer',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    AllocationGroupsList,
  ],
  templateUrl: './contract-public-status-buyer.html',
  styleUrl: './contract-public-status-buyer.scss'
})
export class ContractPublicStatusBuyer implements OnInit, OnDestroy {
  private _route = inject(ActivatedRoute);

  contractId = '';
  contract: PublicBuyContractResponse | null = null;
  allocations: BuyContractAllocation[] = [];

  contractLoading = true;
  allocationsLoading = true;
  refreshing = false;
  notFound = false;

  private pollSub?: Subscription;

  constructor(
    private service: BuyContractService,
    private snackBar: MatSnackBar,
  ) { }

  ngOnInit(): void {
    this.contractId = this._route.snapshot.paramMap.get('contractId')!;
    this.load();
    this.pollSub = timer(POLL_INTERVAL_MS, POLL_INTERVAL_MS).pipe(
      switchMap(() => this.notFound ? EMPTY : [null]),
    ).subscribe(() => this.load());
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
  }

  load(): void {
    // Only show the blocking spinners on the very first load. Later refreshes (poll or
    // manual) update the data in place so the page doesn't jump back to a blank state
    // and lose the user's scroll position / expanded cards.
    const isInitialContractLoad = !this.contract && !this.notFound;
    if (isInitialContractLoad) {
      this.contractLoading = true;
    }

    this.refreshing = true;
    this.service.getPublicBuyContract(this.contractId).subscribe({
      next: contract => {
        this.contract = contract;
        this.contractLoading = false;
        this.refreshing = false;
        this.notFound = false;
      },
      error: (err: HttpErrorResponse) => {
        this.contractLoading = false;
        this.refreshing = false;
        if (err.status === 404) {
          this.notFound = true;
          this.contract = null;
        } else {
          this.snackBar.open('Error loading contract details', 'Close', { duration: 5000 });
        }
      }
    });

    const isInitialAllocationsLoad = this.allocations.length === 0;
    if (isInitialAllocationsLoad) {
      this.allocationsLoading = true;
    }
    this.service.getPublicBuyContractAllocations(this.contractId).subscribe({
      next: res => {
        this.allocations = res.items.filter(a => a.status !== 'Released');
        this.allocationsLoading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.allocationsLoading = false;
        if (err.status !== 404) {
          this.snackBar.open('Error loading allocations', 'Close', { duration: 5000 });
        }
      }
    });
  }

  get progressCoins(): number {
    if (!this.contract) return 0;
    return this.contract.target_type === 'received' ? this.contract.received_coins : this.contract.sent_coins;
  }

  get progressPercent(): number {
    if (!this.contract || !this.contract.target_coins) return 0;
    return Math.min(100, Math.round((this.progressCoins / this.contract.target_coins) * 100));
  }

  getContractStatusClass(status: BuyContractStatus): string {
    switch (status) {
      case 'Active': return 'contract-active';
      case 'Done': return 'contract-done';
      case 'Canceled': return 'contract-canceled';
      case 'Expired': return 'contract-expired';
      case 'Failed': return 'contract-failed';
    }
  }

  formatDate(d?: string): string {
    if (!d) return '—';
    return new Date(d).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }
}
